import React, { useEffect, useState } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Box,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import styles from './SchedulerSetting.module.scss';
import {
  getOnboardingSettings,
  getOnboardingDocuments,
  getOnboardingTemplateTypes,
  deleteOnboardingDocument,
} from '../../../core/API/onboarding';
import {
  IOnboardingSettings,
  IOnboardingDocument,
  IOnboardingTemplateType,
} from 'core/interfaces/onboardingInterface';
import { IHandbookDocument } from 'core/interfaces/handbookInterface';
import { listHandbookDocuments, deleteHandbookDocument } from 'core/API/handbookDocuments';
import { Partner } from 'types';
import { getPartners } from 'core/API/partners';
import EditGeneralSettings from './Onboarding/EditGeneralSettings';
import {
  getStorageProviderDisplayLabel,
} from './Onboarding/onboardingPartnerUtils';
import DocumentsList from './Onboarding/DocumentsList';
import UploadDocument from './Onboarding/UploadDocument';
import EditDocument from './Onboarding/EditDocument';
import HandbookDocumentsList from './Onboarding/HandbookDocumentsList';
import HandbookUploadDocument from './Onboarding/HandbookUploadDocument';
import HandbookEditDocument from './Onboarding/HandbookEditDocument';

type ViewMode =
  | 'list'
  | 'editGeneralSettings'
  | 'uploadDocument'
  | 'editDocument'
  | 'handbookUpload'
  | 'handbookEdit';

const OnboardingSetting = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [settings, setSettings] = useState<IOnboardingSettings | null>(null);
  const [documents, setDocuments] = useState<IOnboardingDocument[]>([]);
  const [templateTypes, setTemplateTypes] = useState<IOnboardingTemplateType[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [editingDocument, setEditingDocument] = useState<IOnboardingDocument | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<IOnboardingDocument | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Handbook state
  const [handbookDocuments, setHandbookDocuments] = useState<IHandbookDocument[]>([]);
  const [editingHandbookDocument, setEditingHandbookDocument] = useState<IHandbookDocument | null>(null);
  const [handbookDeleteDialogOpen, setHandbookDeleteDialogOpen] = useState(false);
  const [handbookDocumentToDelete, setHandbookDocumentToDelete] = useState<IHandbookDocument | null>(null);
  const [isDeletingHandbook, setIsDeletingHandbook] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const workplaceId = Number(localStorage.getItem('workplace-id'));
      const [settingsData, documentsData, typesData, partnersData, handbookData] = await Promise.all([
        getOnboardingSettings(workplaceId),
        getOnboardingDocuments(workplaceId),
        getOnboardingTemplateTypes(),
        getPartners({ active: true }),
        listHandbookDocuments(workplaceId),
      ]);
      setSettings(settingsData);
      setDocuments(
        (documentsData || [])
          .filter((d) => d.active !== false)
          .sort((a, b) => a.sortOrder - b.sortOrder)
      );
      setTemplateTypes(typesData || []);
      setPartners(partnersData || []);
      setHandbookDocuments(handbookData || []);
      setViewMode('list');
    } finally {
      setIsLoading(false);
    }
  };

  const renderSectionLoader = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
      }}
    >
      <CircularProgress size={20} color="inherit" />
    </Box>
  );

  const handleBackClick = () => {
    setViewMode('list');
    setEditingDocument(null);
    setEditingHandbookDocument(null);
  };

  const handleDeleteClick = (document: IOnboardingDocument) => {
    setDocumentToDelete(document);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete?.id) return;

    setIsDeleting(true);
    try {
      await deleteOnboardingDocument(documentToDelete.id);
      await loadData();
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  };

  // Handbook handlers
  const handleHandbookDeleteClick = (document: IHandbookDocument) => {
    setHandbookDocumentToDelete(document);
    setHandbookDeleteDialogOpen(true);
  };

  const handleHandbookDeleteConfirm = async () => {
    if (!handbookDocumentToDelete?.id) return;
    setIsDeletingHandbook(true);
    try {
      await deleteHandbookDocument(handbookDocumentToDelete.id);
      const updated = await listHandbookDocuments(Number(localStorage.getItem('workplace-id')));
      setHandbookDocuments(updated || []);
      setHandbookDeleteDialogOpen(false);
      setHandbookDocumentToDelete(null);
    } catch (error) {
      console.error('Error deleting handbook document:', error);
      alert('Failed to delete handbook document');
    } finally {
      setIsDeletingHandbook(false);
    }
  };

  const handleHandbookDeleteCancel = () => {
    setHandbookDeleteDialogOpen(false);
    setHandbookDocumentToDelete(null);
  };

  // Render edit views
  if (viewMode === 'editGeneralSettings') {
    return (
      <div className={styles.schedulerSettings}>
        <EditGeneralSettings
          settings={settings}
          allPartners={partners}
          handleBackClick={handleBackClick}
          onSettingsUpdated={loadData}
        />
      </div>
    );
  }

  if (viewMode === 'uploadDocument') {
    return (
      <div className={styles.schedulerSettings}>
        <UploadDocument
          existingDocumentsCount={documents.length}
          existingNames={documents.map((d) => d.name)}
          templateTypes={templateTypes}
          handleBackClick={handleBackClick}
          onDocumentUploaded={loadData}
        />
      </div>
    );
  }

  if (viewMode === 'editDocument' && editingDocument) {
    return (
      <div className={styles.schedulerSettings}>
        <EditDocument
          document={editingDocument}
          existingNames={documents.map((d) => d.name).filter((name) => name !== editingDocument.name)}
          handleBackClick={handleBackClick}
          onDocumentUpdated={loadData}
        />
      </div>
    );
  }

  if (viewMode === 'handbookUpload') {
    return (
      <div className={styles.schedulerSettings}>
        <HandbookUploadDocument
          existingNames={handbookDocuments.map((d) => d.name)}
          handleBackClick={handleBackClick}
          onDocumentUploaded={loadData}
        />
      </div>
    );
  }

  if (viewMode === 'handbookEdit' && editingHandbookDocument) {
    return (
      <div className={styles.schedulerSettings}>
        <HandbookEditDocument
          document={editingHandbookDocument}
          existingNames={handbookDocuments
            .map((d) => d.name)
            .filter((name) => name !== editingHandbookDocument.name)}
          handleBackClick={handleBackClick}
          onDocumentUpdated={loadData}
        />
      </div>
    );
  }

  // Main list view
  return (
    <div className={styles.schedulerSettings}>
      {/* General Settings (EIN + Storage Provider) */}
      <Card className="card">
        <div className={styles.header}>
          <h2 className={styles.formTitle}>General Settings</h2>
        </div>
        <div className={styles.list}>
          {isLoading ? (
            renderSectionLoader()
          ) : (
            <div className={styles.tableContainer}>
              <table className={styles.workplaceTable}>
                <thead>
                  <tr>
                    <th>Employer Identification Number (EIN)</th>
                    <th>Storage Provider</th>
                    <th style={{ width: 48, textAlign: 'center' }}>Edit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <span>{settings?.ein || 'Not configured'}</span>
                    </td>
                    <td>
                      <span>
                        {getStorageProviderDisplayLabel(settings, partners)}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
                      <button
                        className={styles.iconButton}
                        title="Edit"
                        onClick={() => {
                          setViewMode('editGeneralSettings');
                        }}
                      >
                        <Edit className={styles.editIcon} />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {/* Onboarding Documents */}
      <Card className="card">
        <div className={styles.header}>
          <h2 className={styles.formTitle}>
            Onboarding Documents ({documents.length}/50)
          </h2>
          <Button
            className={styles.addButton}
            onClick={() => setViewMode('uploadDocument')}
            disabled={isLoading || documents.length >= 50}
          >
            Add
          </Button>
        </div>
        <div className={styles.list}>
          {isLoading ? (
            renderSectionLoader()
          ) : (
            <DocumentsList
              documents={documents}
              templateTypes={templateTypes}
              onEditClick={(doc) => {
                setEditingDocument(doc);
                setViewMode('editDocument');
              }}
              onDeleteClick={handleDeleteClick}
            />
          )}
        </div>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{documentToDelete?.name}&quot;?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button className={styles.mabelButton} onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button className={styles.mabelButton} onClick={handleDeleteConfirm} color="error" disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Employee Handbook Documents */}
      <Card className="card">
        <div className={styles.header}>
          <h2 className={styles.formTitle}>
            Employee Handbook Documents ({handbookDocuments.length})
          </h2>
          <Button
            className={styles.addButton}
            onClick={() => setViewMode('handbookUpload')}
            disabled={isLoading}
          >
            Add
          </Button>
        </div>
        <div className={styles.list}>
          {isLoading ? (
            renderSectionLoader()
          ) : (
            <HandbookDocumentsList
              documents={handbookDocuments}
              onEditClick={(doc) => {
                setEditingHandbookDocument(doc);
                setViewMode('handbookEdit');
              }}
              onDeleteClick={handleHandbookDeleteClick}
            />
          )}
        </div>
      </Card>

      {/* Handbook Delete Confirmation Dialog */}
      <Dialog open={handbookDeleteDialogOpen} onClose={handleHandbookDeleteCancel}>
        <DialogTitle>Delete Handbook Document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{handbookDocumentToDelete?.name}&quot;?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button className={styles.mabelButton} onClick={handleHandbookDeleteCancel} disabled={isDeletingHandbook}>
            Cancel
          </Button>
          <Button className={styles.mabelButton} onClick={handleHandbookDeleteConfirm} color="error" disabled={isDeletingHandbook}>
            {isDeletingHandbook ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OnboardingSetting;
